/**
 * useFoundationAutoRoute — Round 218A P0 FINAL (2026-02-07),
 * scope-tightened 2026-02-07 (Phase 62).
 *
 * SCOPE: ONBOARDING ONLY.
 *
 * PayGBot decides where the customer goes next ONLY while the
 * foundation is still being built. Once ``foundation_complete ===
 * true`` the customer is free to open any builder, bot, or
 * dashboard card at any time — this hook is a strict no-op.
 *
 * When a downstream Money Bot builder opens AND the foundation is
 * INCOMPLETE (i.e. the customer is still inside onboarding), this
 * hook silently:
 *   1. Closes the just-opened modal.
 *   2. Emits a friendly toast: "Continue to Brand Builder — I'll
 *      take you there."
 *   3. Navigates to the resolver's next_route.
 *
 * When the foundation IS complete, the hook returns immediately.
 * The customer's intentional selection is honored. No blocker, no
 * redirect, no restriction — that's the flexible product experience
 * PayGBot promises after onboarding.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useNextAction from './useNextAction';

export default function useFoundationAutoRoute({ isOpen, onClose }) {
  const nav = useNextAction(isOpen);
  const navigate = useNavigate();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      firedRef.current = false;
      return;
    }
    if (nav.loading) return;
    // Post-onboarding: strict no-op. The customer chose this
    // builder — respect that choice.
    if (nav.foundationComplete) return;
    if (!nav.nextRoute) return;
    if (firedRef.current) return;
    firedRef.current = true;

    const label = nav.nextCtaLabel || 'the next step';
    toast.info(`${label} — I'll take you there.`, { duration: 2600 });

    if (typeof onClose === 'function') {
      try { onClose(); } catch (_) { /* noop */ }
    }
    setTimeout(() => navigate(nav.nextRoute), 60);
  }, [isOpen, nav.loading, nav.foundationComplete, nav.nextRoute, nav.nextCtaLabel, onClose, navigate]);

  return nav;
}
